// Component Name:  AddLocation component
// Author: Bharath
// Platform: Web
// OS: Windows
// IDE: Visual Studio Code
// Reference: Original
// Date:
// Description: Dashboard
// Dependencies: React,  Redux (for warningToast), API layer to fetch and add both location and image
// Interfaces: Accepts props including selectedLocationId, locationSelected, email
// Maintenance History:  1.0 - First Version

// Edited by Lavanya S
// Converted from class component to functional component
// Integrated InfoBar component
// Integrated API to fetch transaction and fault logs
// Real-time updates: InfoBar and widgets display data calculated directly from live API responses
// transaction and fault logs data will get updated based on the filters applied.
import React, { useCallback, useEffect, useState } from "react";
import { Container, Row, Col } from "reactstrap";

//Import Breadcrumb
import Breadcrumbs from "../../components/Common/Breadcrumb";

//Import Components
import MiniWidgets from "./MiniWidgets";
import RevenueAnalytics from "./RevenueAnalytics";
import SalesAnalytics from "./SalesAnalytics";
import EarningReports from "./EarningReports";
import Sources from "./Sources";
import RecentlyActivity from "./RecentlyActivity";
import RevenueByLocations from "./RevenueByLocations";
import FaultTrend from "./FaultTrend";
import FaultAnalytics from "./FaultAnalytics";
import FaultBar from "./FaultBar";
import ConnectivityStats from "./ConnectivityStats";
import ActiveKnob from "./ActiveKnob";
import ChatBox from "./ChatBox";
import LatestTransactions from "./LatestTransactions";
import { connect } from "react-redux";
import { isTechnician } from "../Authentication/userMetaData";
import { lkRed } from "../Utility/styleComponents";
import InfoBar from "./InfoBar";
import { addChargerIds, chargerSelected, toggleInfobar } from "../../store/actions";
import LatestFaultLog from "./LatestFaultLog";
import { getStatusByOwnerId } from "../../api/statusAPI";
import { getTransactionByOwnerId } from "../../api/transactionAPI";
import { computeRevenue, computeUnits } from "../Utility/utilsFunctions";
import { locationSelected } from "../../store/locations/actions";
import { loadTransactions } from "../../api/utils/loadTransactions";
import { loadFaults } from "../../api/utils/loadFaults";
// import { addChargerIds } from "../../store/dbFilter/actions";
import { getLocationById } from "../../api/locationAPI";
import Spinner from "../Utility/Spinner";
import useIsMounted from "../Utility/useIsMounted";

import LKBillingImg from "../../assets/images/LK_Icons/LK_Billing.svg";
import LKRevenueImg from "../../assets/images/LK_Icons/LK__Savings.svg";
import LKSessionImg from "../../assets/images/LK_Icons/LK_Records.svg";

const Dashboard = (props) => {
  const { user, selectedLocationId, locationSelected, chargerIds } = props;
  const [dateRange, setDateRange] = useState([]);
  const [location, setLocation] = useState("");
  const [locationName, setLocationName] = useState("All");
  const [isLoading, setIsLoading] = useState(false);
  const isMounted = useIsMounted();
  // console.log("isLoading: ", isLoading);
  // console.log("location: ", location);
  // console.log("locationName: ", locationName, selectedLocationId);

  // dashboard data on the infobar
  const [session, setSession] = useState(0);
  const [units, setUnits] = useState(0);
  const [co2Saving, setCo2Saving] = useState(0);
  const [revenue, setRevenue] = useState(0);

  // dashboard data in the widget and values changes with the filter
  const [widgetSession, setWidgetSession] = useState(0);
  const [widgetUnits, setWidgetUnits] = useState(0);
  const [widgetCo2Saving, setWidgetCo2Saving] = useState(0);
  const [widgetRevenue, setWidgetRevenue] = useState(0);

  // widgets data
  const reports = [
    {
      // image: LKSessionImg,
      icon: "ri-stack-line",
      title: "Number of Sessions",
      value: widgetSession,
      rate: "12.33%",
      desc: "From last Month",
      rateChange: "up",
      // theme: "text-success",
    },
    {
      image: LKRevenueImg,
      title: "Total Revenue",
      value: `\u20B9${widgetRevenue}`,
      rate: "3.43%",
      desc: "From last Month",
      rateChange: "down",
    },
    {
      image: LKBillingImg,
      title: "Avg. Revenue per Session",
      value: `\u20B9${Number((widgetRevenue / widgetSession).toFixed(2)) || 0}`,
      rate: "32.4%",
      desc: "From previous period",
      rateChange: "up",
    },
  ];

  // widgets data for the technician
  const faultReports = [
    {
      icon: "ri-charging-pile-2-fill",
      title: "Number of Chargers",
      value: "3",
      rate: "12.33%",
      desc: "From last Month",
      rateChange: "up",
    },
    {
      icon: "ri-arrow-down-circle-fill",
      title: "Total Downtime (hrs)",
      value: "10",
      rate: "10.59%",
      desc: "From last Month",
      rateChange: "up",
    },
    {
      icon: "ri-error-warning-fill",
      title: "Number of Faults",
      value: "13",
      rate: "32.4%",
      desc: "From previous period",
      rateChange: "up",
    },
  ];

  // stores recent transaction and fault log
  const [latestTrans, setLatestTrans] = useState([]);
  const [filteredTrans, setFilteredTrans] = useState([]);
  const [faultLog, setFaultLog] = useState([]);

  // to reset/clear the filter for the dashboard
  const handleResetFilter = () => {
    const today = new Date();
    const firstDayOfMonth = new Date("2024-01-01");
    handleDateRange(firstDayOfMonth, today);
    setLocation("");
    setLocationName("All");
    locationSelected("");
  };

  // update the selected daterange
  const handleDateRange = (startDate, endDate) => {
    if (isNaN(startDate?.getTime())) {
      startDate = new Date("2024-01-01");
    }
    if (isNaN(endDate?.getTime())) {
      endDate = new Date();
    }
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(23, 59, 59, 999);
    setDateRange([startDate, endDate]);
  };
  // update the selected location
  const handleLocation = async (locationId) => {
    locationSelected(locationId);
    if (locationId?.length > 0) {
      try {
        const res = await getLocationById(locationId);
        setLocationName(res?.name);
      } catch (err) {
        console.log("err getting the location ", err);
      }
    } else {
      setLocationName("All");
    }
    setLocation(locationId);
  };

  // On Component mount, set the daterange and location, and fetch the transaction log and fault log.
  // when component unmount, clear the interval and reset all teh data.
  useEffect(() => {
    // setIsLoading(true)
    // const timeOut = setTimeout(()=>{
    //   setIsLoading(false)
    // },2000)
    if (selectedLocationId?.length > 0) {
      isMounted.current && handleLocation(selectedLocationId);
    }
    fetchData(); // Initial fetch
    const interval = setInterval(async () => {
      await fetchData(); // Repeated fetch every 5000ms
    }, 5000);
    return () => {
      clearInterval(interval);
      handleResetFilter();
      // clearTimeout(timeOut)
    };
  }, [chargerIds]);

  // call to fetch the recent transaction and fault data
  const fetchData = async () => {
    try {
      await updateTrasactionLog();
      await updateChargerFaultLog();
    } catch (error) {
      console.error("Main Dash Update Error", error.toString());
    }
  };

  // To get the fault log using hostId
  const updateChargerFaultLog = async () => {
    try {
      // let ownerIdStatusData = {
      //   ownerId: 9847040400,
      //   type: "Faulted",
      // };
      const allStatus = await loadFaults(chargerIds);
      //Sort by StartTime
      allStatus.sort((a, b) => {
        if (a.timeStamp && b.timeStamp) {
          return -a.timeStamp.localeCompare(b.timeStamp);
        }
        return 0; // or handle as necessary if startTime is missing
      });
      if (isMounted.current) {
        setFaultLog((faultLog) => {
          if (JSON.stringify(faultLog) === JSON.stringify(allStatus)) return faultLog;
          return allStatus;
        });
      }
    } catch (err) {
      console.log("Error getting the Status ", err.msg);
    }
  };
  // to fetch the transaction logs
  const updateTrasactionLog = async () => {
    // const ownerIdTransactionData = {
    //   ownerId: 9847040400,
    //   // limit: 100,
    //   // page: 1,
    // };
    try {
      const allTransactions = await loadTransactions(chargerIds);
      if (isMounted.current) {
        setLatestTrans((latestTrans) => {
          if (JSON.stringify(allTransactions) === JSON.stringify(latestTrans)) return latestTrans;
          return allTransactions;
        });
      }

      updateTransactionDetails(allTransactions);
    } catch (err) {
      console.log("Error to retrive transaction by ownerId: ", err.msg);
    }
  };

  // handle the infobar toggle
  const toggleInfobarCallback = () => {
    if (props.infobarType === "collapsed") {
      props.toggleInfobar("expanded");
    } else if (props.infobarType === "expanded") {
      props.toggleInfobar("collapsed");
    }
  };

  // cal units, revenue and co2Saving
  const calcTransactionDetails = (allTransactions) => {
    // units
    const totalUnits = allTransactions?.reduce((acc, item) => {
      const startMeter = parseInt(item.meterStart, 10);
      const stopMeter = parseInt(item.meterStop, 10);
      return acc + computeUnits(startMeter, stopMeter);
    }, 0);
    const unit = totalUnits.toFixed(2);
    // revenue
    const totalRevenue = allTransactions?.reduce((acc, item) => {
      const startMeter = parseInt(item.meterStart, 10);
      const stopMeter = parseInt(item.meterStop, 10);
      const perUnitRate = item?.perUnitRate;
      return acc + computeRevenue(startMeter, stopMeter, perUnitRate);
    }, 0);
    // console.log(("transaction totalRevenue: ", totalRevenue));
    const revenue = totalRevenue.toFixed(2);
    const co2Saving = (totalUnits * 0.2944).toFixed(2);

    return { unit, revenue, co2Saving };
  };

  // update the infobar details on the transaction data change
  const updateTransactionDetails = useCallback(
    (allTransactions) => {
      const { unit, revenue, co2Saving } = calcTransactionDetails(allTransactions);
      if (isMounted.current) {
        setSession(allTransactions?.length);
        setUnits(unit);
        setRevenue(revenue);
        setCo2Saving(co2Saving);
      }
    },
    [setLatestTrans]
  );

  //this will be triggered on daterange/transaction change,
  //filter and update the dashboard data
  useEffect(() => {
    let chargersInSelectedLocation = [];
    if (selectedLocationId?.length > 0) {
      // chargersInSelectedLocation = props.chargerToLocationMap[selectedLocationId] || [];

      const chargersInSelectedLoc = props.chargerToLocationMap[selectedLocationId] || [];
      chargersInSelectedLocation = chargersInSelectedLoc?.map((item) => item?.id) || [];
    }
    const startDate = new Date(dateRange[0]).getTime();
    const endDate = new Date(dateRange[1]).getTime();

    // filter the transaction for the selected date range
    let filteredTransaction = [];
    filteredTransaction = latestTrans.filter((trans, index) => {
      const statusTime = new Date(trans.startTime).getTime();
      if (selectedLocationId?.length === 0) {
        return statusTime >= startDate && statusTime <= endDate;
      }
      return statusTime >= startDate && statusTime <= endDate && chargersInSelectedLocation?.includes(trans?.chargerId);
    });
    const { unit, revenue, co2Saving } = calcTransactionDetails(filteredTransaction);
    if (isMounted.current) {
      setFilteredTrans(filteredTransaction);
      setWidgetSession(filteredTransaction?.length);
      setWidgetUnits(unit);
      setWidgetRevenue(revenue);
      setWidgetCo2Saving(co2Saving);
    }
  }, [dateRange, latestTrans, selectedLocationId]);

  return (
    <React.Fragment>
      {isLoading ? (
        <div style={{ height: "100vh" }}>
          <Spinner />
        </div>
      ) : (
        <div className={`page-content ${!isTechnician(user) && "dashboard-infobar"}`}>
          <Container fluid>
            {
              props.layoutType === "vertical" &&
              !isTechnician(user) && (
                <Row id="page-infobar">
                  <InfoBar
                    toggleInfobarCallback={toggleInfobarCallback}
                    transactionData={{ session, units, co2Saving, revenue, locationName }}
                    filterData={{ dateRange, handleDateRange, location, handleLocation, handleResetFilter }}
                    history={props.history}
                  />
                </Row>
              )
            }

            {/* <Breadcrumbs title="Dashboard" breadcrumbItems={breadcrumbItems} /> */}
            <Row>
              <Col xl={8}>
                {!isTechnician(user) && (
                  <Row>
                    <MiniWidgets reports={reports} />
                  </Row>
                )}

                {/* revenue Analytics */}
                {!isTechnician(user) && <RevenueAnalytics transactionData={latestTrans} dbFilter={{ dateRange, location }} />}
                {isTechnician(user) && <FaultTrend />}
                {isTechnician(user) && (
                  <Row>
                    <MiniWidgets reports={faultReports} clr={lkRed} />
                  </Row>
                )}
              </Col>
              {!isTechnician(user) && (
                <Col xl={4}>
                  {/* earning reports */}
                  <EarningReports ht="11" units={widgetUnits} co2Saving={widgetCo2Saving} />
                  {/* sales Analytics */}
                  <SalesAnalytics dbFilter={{ dateRange, location }} />
                </Col>
              )}

              {isTechnician(user) && (
                <Col xl={4}>
                  {/* earning reports */}
                  {<ConnectivityStats ht="40" />}

                  {/* Fault Analytics */}
                  <FaultAnalytics />

                  {/* earning reports */}
                  {/* <EarningReports /> */}
                </Col>
              )}
            </Row>
            <Row>
              {/* chat box */}
              {/*<ChatBox />*/}

              {/* latest transactions */}
              <LatestTransactions transactionData={filteredTrans} history={props.history} dbFilter={{ dateRange, location }} />
            </Row>
            <Row>
              <Col lg={6}>
                <FaultBar faultData={faultLog} dbFilter={{ dateRange, location }} />
              </Col>

              <Col lg={6}>
                <ActiveKnob energyConsumed={widgetUnits} dbFilter={{ dateRange, location }} />
              </Col>
            </Row>

            {/* {!isTechnician(this.state.user) && (
              <Row>
                //  sources 

                <Sources />

              //  recent activity 
                <RecentlyActivity />

                // revenue by locations 
                <RevenueByLocations />
              </Row>
            )} */}

            <Row>
              <LatestFaultLog faultData={faultLog} history={props.history} dbFilter={{ dateRange, location }} />
            </Row>
          </Container>
        </div>
      )}
    </React.Fragment>
  );
};

const mapStateToProps = (state) => ({
  user: state.Login.user,
  infobarType: state.Layout.infobarType,
  layoutType: state.Layout.layoutType,
  selectedLocationId: state?.LocationManagement?.selectedLocationId,
  chargerIds: state?.ChargerManagement?.chargerIds,
  chargerToLocationMap: state?.sharedData?.chargerToLocationMap,
});
const mapDispatchToProps = (dispatch) => ({
  toggleInfobar: (payload) => dispatch(toggleInfobar(payload)),
  chargerSelected: (payload) => dispatch(chargerSelected(payload)),
  locationSelected: (payload) => dispatch(locationSelected(payload)),
  addChargerIds: (payload) => dispatch(addChargerIds(payload)),
});
export default connect(mapStateToProps, mapDispatchToProps)(Dashboard);
